import { useTranslation } from "react-i18next";

import {
	PRODUCT_USAGE_PERIODS,
	PRODUCT_USAGE_ALL_CUSTOMERS_PERIODS,
	type ProductUsageFilters,
	type ProductUsagePlanOption,
	type ProductUsageAllCustomersPeriod,
} from "@signalist/shared-types";

import { SelectField } from "@/components/SelectField/SelectField";
import { Toggle } from "@/components/Toggle/Toggle";

import { MetricHelp } from "./MetricHelp";

export function PeriodFilter<P extends ProductUsageAllCustomersPeriod>({
	value,
	onChange,
	all = false,
}: {
	value: P;
	onChange: (value: P) => void;
	all?: boolean;
}) {
	const { t } = useTranslation();

	return (
		<div className="w-40">
			<SelectField
				hideEmptyValue
				portal
				label={t("featureUsage.period")}
				selectedOption={value}
				options={(all ? PRODUCT_USAGE_ALL_CUSTOMERS_PERIODS : PRODUCT_USAGE_PERIODS).map(
					(period) => ({
						value: period,
						title: t(period === 365 ? "featureUsage.year" : "featureUsage.days", { count: period }),
					})
				)}
				handleSelect={(period) => onChange(Number(period) as P)}
				testId="usage-period"
			/>
			<MetricHelp label={t("featureUsage.period")} definition={t("featureUsage.help.period")} />
		</div>
	);
}
export function UsageFilters({
	value,
	onChange,
	plans,
}: {
	value: ProductUsageFilters;
	onChange: (filters: ProductUsageFilters) => void;
	plans: readonly ProductUsagePlanOption[];
}) {
	const { t } = useTranslation();

	return (
		<div className="mb-4 flex flex-wrap items-center gap-4">
			<PeriodFilter value={value.period} onChange={(period) => onChange({ ...value, period })} />
			<div className="w-48">
				<SelectField
					hideEmptyValue
					portal
					label={t("featureUsage.plan")}
					placeholder={t("featureUsage.allPlans")}
					selectedOption={value.planId}
					options={[
						{ value: null, title: t("featureUsage.allPlans") },
						...plans.map((plan) => ({ value: plan.planId, title: plan.name })),
					]}
					handleSelect={(planId) =>
						onChange({ ...value, planId: planId === null ? null : Number(planId) })
					}
					testId="usage-plan"
				/>
			</div>
			{(["includeTrialDemo", "includeInternal"] as const).map((key) => {
				const copy = key === "includeTrialDemo" ? "trial" : "internal";
				const toggle = () => onChange({ ...value, [key]: !value[key] });

				return (
					<div key={key} className="flex items-center gap-2 text-xs text-text-meta">
						<div
							aria-checked={value[key]}
							aria-label={t(`featureUsage.${copy}`)}
							className="cursor-pointer"
							role="switch"
							tabIndex={0}
							onKeyDown={(event) => {
								if (event.key === " " || event.key === "Enter") {
									event.preventDefault();
									toggle();
								}
							}}
						>
							<Toggle isChecked={value[key]} name={t(`featureUsage.${copy}`)} onChange={toggle} />
						</div>
						<span>{t(`featureUsage.${copy}`)}</span>
						<MetricHelp
							label={t(`featureUsage.${copy}`)}
							definition={t(`featureUsage.help.${copy}`)}
						/>
					</div>
				);
			})}
		</div>
	);
}
